import React, { useRef, useState, useEffect, useCallback } from "react";
import { FantasyPin, CategoryIcon, getCategory } from "./PinIcon";
import { formatDistance } from "./formatDuration";

/**
 * Interactive map canvas with pan/zoom, SVG overlay for pins and paths.
 * Coordinates are stored as fractions (0..1) of the image dimensions.
 *
 * Props:
 * - imageUrl: string
 * - tool: 'view' | 'pin' | 'path' | 'ruler'
 * - locations, paths
 * - draftPath: {points:[{x,y}]} | null
 * - rulerPoints: [{x,y}, {x,y}] | []
 * - scaleInfo: {scale_pixels, scale_distance, scale_unit}
 * - settings: {pin_size, pin_style, path_color, path_point_size}
 * - isNight: bool -> applies blue overlay
 * - selectedLocationId: id -> highlights pin
 * - onCanvasClick(fx, fy)
 * - onLocationClick(loc) (opens info panel, NOT direct edit)
 * - onLocationDrag(locId, fx, fy)
 */
export default function MapCanvas({
  imageUrl,
  tool,
  locations = [],
  paths = [],
  draftPath = null,
  rulerPoints = [],
  scaleInfo,
  settings,
  isNight,
  selectedLocationId,
  focusTarget,
  hideSavedPaths,
  visiblePathIds,
  onCanvasClick,
  onLocationClick,
  onLocationDrag,
  onPathPointDrag,
  onImgSizeChange,
}) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [transform, setTransform] = useState({ scale: 1, tx: 0, ty: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef(null);
  const [draggingLocId, setDraggingLocId] = useState(null);
  const [draggingPathIdx, setDraggingPathIdx] = useState(null);
  const [hoverLocId, setHoverLocId] = useState(null);
  const pendingDragRef = useRef(null); // {locId, startX, startY}

  const pinsInteractive = tool === "view" || tool === "pin";

  const handleImgLoad = () => {
    const el = imgRef.current;
    if (!el) return;
    const w = el.naturalWidth;
    const h = el.naturalHeight;
    setImgSize({ w, h });
    onImgSizeChange?.({ w, h });
    const wrap = wrapRef.current;
    if (wrap) {
      const rect = wrap.getBoundingClientRect();
      const scale = Math.min(rect.width / w, rect.height / h, 1);
      setTransform({
        scale,
        tx: (rect.width - w * scale) / 2,
        ty: (rect.height - h * scale) / 2,
      });
    }
  };

  const clientToImageFrac = useCallback(
    (clientX, clientY) => {
      const wrap = wrapRef.current;
      if (!wrap || !imgSize.w) return { x: 0, y: 0 };
      const rect = wrap.getBoundingClientRect();
      const localX = clientX - rect.left;
      const localY = clientY - rect.top;
      const imgX = (localX - transform.tx) / transform.scale;
      const imgY = (localY - transform.ty) / transform.scale;
      return {
        x: Math.max(0, Math.min(1, imgX / imgSize.w)),
        y: Math.max(0, Math.min(1, imgY / imgSize.h)),
      };
    },
    [transform, imgSize]
  );

  const handleWheel = (e) => {
    e.preventDefault();
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const delta = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    const newScale = Math.min(6, Math.max(0.1, transform.scale * delta));
    const ratio = newScale / transform.scale;
    setTransform({
      scale: newScale,
      tx: cx - (cx - transform.tx) * ratio,
      ty: cy - (cy - transform.ty) * ratio,
    });
  };

  const handleMouseDown = (e) => {
    if (e.button === 2 || (tool === "view" && e.button === 0) || e.button === 1) {
      e.preventDefault();
      setIsPanning(true);
      panRef.current = { x: e.clientX, y: e.clientY, tx: transform.tx, ty: transform.ty };
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning && panRef.current) {
      const { x: startX, y: startY, tx: startTx, ty: startTy } = panRef.current;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      setTransform((t) => ({ ...t, tx: startTx + dx, ty: startTy + dy }));
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
    panRef.current = null;
    setDraggingLocId(null);
    setDraggingPathIdx(null);
    pendingDragRef.current = null;
  };

  const handleCanvasClick = (e) => {
    if (isPanning || draggingLocId || draggingPathIdx !== null) return;
    if (tool === "view") return;
    // Ignore clicks that occurred on SVG interactive elements (pins, draft points)
    // by checking for the specific data-testid marker on the target
    const t = e.target;
    if (t && t.getAttribute && (t.getAttribute("data-testid") || "").startsWith("map-pin-")) return;
    if (t && t.getAttribute && (t.getAttribute("data-testid") || "").startsWith("draft-path-point-")) return;
    const { x, y } = clientToImageFrac(e.clientX, e.clientY);
    onCanvasClick?.(x, y);
  };

useEffect(() => {
  const onKey = (e) => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    // Non controllare la mappa mentre si sta scrivendo
    const target = e.target;

    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      target.isContentEditable
    ) {
      return;
    }

    const step = 40;

    if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      setTransform((t) => ({
        ...t,
        scale: Math.min(6, t.scale * 1.1),
      }));
    } else if (e.key === "-") {
      e.preventDefault();
      setTransform((t) => ({
        ...t,
        scale: Math.max(0.1, t.scale / 1.1),
      }));
    } else if (e.key === "0") {
      e.preventDefault();
      fitToScreen();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setTransform((t) => ({ ...t, ty: t.ty + step }));
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setTransform((t) => ({ ...t, ty: t.ty - step }));
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setTransform((t) => ({ ...t, tx: t.tx + step }));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setTransform((t) => ({ ...t, tx: t.tx - step }));
    }
  };

  window.addEventListener("keydown", onKey);

  return () => window.removeEventListener("keydown", onKey);

  // eslint-disable-next-line
}, [imgSize]);

  const fitToScreen = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap || !imgSize.w) return;
    const rect = wrap.getBoundingClientRect();
    const s = Math.min(rect.width / imgSize.w, rect.height / imgSize.h, 1);
    setTransform({
      scale: s,
      tx: (rect.width - imgSize.w * s) / 2,
      ty: (rect.height - imgSize.h * s) / 2,
    });
  }, [imgSize]);

  // Focus on target (x, y in fractions) - zoom in and center
  useEffect(() => {
    if (!focusTarget || !imgSize.w) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const desiredScale = focusTarget.zoom || 2.2;
    const cx = focusTarget.x * imgSize.w * desiredScale;
    const cy = focusTarget.y * imgSize.h * desiredScale;
    setTransform({
      scale: desiredScale,
      tx: rect.width / 2 - cx,
      ty: rect.height / 2 - cy,
    });
  }, [focusTarget?.key, focusTarget?.x, focusTarget?.y, focusTarget?.zoom, imgSize.w, imgSize.h]);

  // Public API for parent (via ref)
  React.useImperativeHandle?.(null, () => ({}));

  const distanceLabel = (fx1, fy1, fx2, fy2) => {
    if (!scaleInfo || !scaleInfo.scale_pixels || !imgSize.w) return "";
    const dxPx = (fx2 - fx1) * imgSize.w;
    const dyPx = (fy2 - fy1) * imgSize.h;
    const distPx = Math.sqrt(dxPx * dxPx + dyPx * dyPx);
    const dist = (distPx / scaleInfo.scale_pixels) * scaleInfo.scale_distance;
    return formatDistance(dist, scaleInfo.scale_unit);
  };

  const cursorClass = {
    view: isPanning ? "cursor-grabbing" : "cursor-grab",
    pin: "cursor-crosshair",
    path: "cursor-crosshair",
    ruler: "cursor-crosshair",
  }[tool] || "cursor-default";

  const pinSize = 28 * (settings?.pin_size || 1);
  const pinStyle = settings?.pin_style || "shield";
  const pathColor = settings?.path_color || "#d4af37";
  const pathPointRadius = 4 * (settings?.path_point_size || 1);

  // Locations render order (hovered goes last -> on top)
  const orderedLocations = [...locations].sort((a, b) => {
    if (a.id === hoverLocId) return 1;
    if (b.id === hoverLocId) return -1;
    return 0;
  });

  return (
    <div
      ref={wrapRef}
      className={`relative w-full h-full bg-background overflow-hidden select-none ${cursorClass}`}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={(e) => {
        if (draggingLocId) {
          const { x, y } = clientToImageFrac(e.clientX, e.clientY);
          onLocationDrag?.(draggingLocId, x, y);
        } else if (draggingPathIdx !== null) {
          const { x, y } = clientToImageFrac(e.clientX, e.clientY);
          onPathPointDrag?.(draggingPathIdx, x, y);
        } else if (pendingDragRef.current) {
          const { locId, startX, startY } = pendingDragRef.current;
          const dx = e.clientX - startX;
          const dy = e.clientY - startY;
          if (dx * dx + dy * dy > 25) { // > 5px threshold
            setDraggingLocId(locId);
            pendingDragRef.current = null;
          }
        } else {
          handleMouseMove(e);
        }
      }}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onContextMenu={(e) => e.preventDefault()}
      onClick={handleCanvasClick}
      data-testid="map-canvas"
    >
      {/* Grid background */}
      <div className="absolute inset-0 grid-overlay opacity-20 pointer-events-none" />

      {/* Image + overlay */}
      {imageUrl && (
        <div
          className="absolute top-0 left-0"
          style={{
            transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})`,
            transformOrigin: "0 0",
          }}
          data-canvas-surface="true"
        >
          <img
            ref={imgRef}
            src={imageUrl}
            onLoad={handleImgLoad}
            alt="Mappa"
            className="block max-w-none pointer-events-none"
            draggable={false}
          />

          {/* Night filter overlay */}
          {isNight && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                width: imgSize.w,
                height: imgSize.h,
                background:
                  "linear-gradient(135deg, rgba(30,27,75,0.35) 0%, rgba(15,10,30,0.4) 100%)",
                mixBlendMode: "multiply",
              }}
              data-testid="night-filter"
            />
          )}

          {imgSize.w > 0 && (
            <svg
              className="absolute top-0 left-0"
              width={imgSize.w}
              height={imgSize.h}
              style={{ overflow: "visible", pointerEvents: "none" }}
            >
              {/* Saved paths (hidden by default; shown only if in visiblePathIds set) */}
              {!hideSavedPaths && paths.filter((p) => visiblePathIds?.has ? visiblePathIds.has(p.id) : false).map((p) => {
                if (!p.points || p.points.length < 2) return null;
                const points = p.points
                  .map((pt) => `${pt.x * imgSize.w},${pt.y * imgSize.h}`)
                  .join(" ");
                return (
                  <g key={p.id}>
                    <polyline
                      points={points}
                      fill="none"
                      stroke={p.color || pathColor}
                      strokeWidth={Math.max(2, 3 / transform.scale)}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      strokeDasharray="8 4"
                      opacity="0.85"
                    />
                    {p.points.map((pt, i) => (
                      <circle
                        key={i}
                        cx={pt.x * imgSize.w}
                        cy={pt.y * imgSize.h}
                        r={Math.max(3, pathPointRadius / transform.scale)}
                        fill={p.color || pathColor}
                        stroke="#000"
                        strokeWidth={Math.max(1, 1 / transform.scale)}
                      />
                    ))}
                  </g>
                );
              })}

              {/* Draft path with draggable points */}
              {draftPath && draftPath.points && draftPath.points.length > 0 && (
                <g style={{ pointerEvents: tool === "path" ? "auto" : "none" }}>
                  {draftPath.points.length >= 2 && (
                    <polyline
                      points={draftPath.points
                        .map((pt) => `${pt.x * imgSize.w},${pt.y * imgSize.h}`)
                        .join(" ")}
                      fill="none"
                      stroke={pathColor}
                      strokeWidth={Math.max(2, 3 / transform.scale)}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  )}
                  {draftPath.points.map((pt, i) => (
                    <g key={i}>
                      <circle
                        cx={pt.x * imgSize.w}
                        cy={pt.y * imgSize.h}
                        r={Math.max(5, (pathPointRadius + 2) / transform.scale)}
                        fill={pathColor}
                        stroke="#000"
                        strokeWidth={Math.max(1, 1 / transform.scale)}
                        style={{ cursor: "grab" }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setDraggingPathIdx(i);
                        }}
                        data-testid={`draft-path-point-${i}`}
                      />
                      {i > 0 && (() => {
                        const label = distanceLabel(draftPath.points[i - 1].x, draftPath.points[i - 1].y, pt.x, pt.y);
                        const fs = Math.max(10, 14 / transform.scale);
                        const w = label.length * fs * 0.6 + 8 / transform.scale;
                        const h = fs + 6 / transform.scale;
                        const tx = pt.x * imgSize.w + 8 / transform.scale;
                        const ty = pt.y * imgSize.h - 8 / transform.scale;
                        return (
                          <g>
                            <rect
                              x={tx - 2 / transform.scale}
                              y={ty - fs}
                              width={w}
                              height={h}
                              rx={3 / transform.scale}
                              fill="rgba(15,12,20,0.85)"
                              stroke="#d4af37"
                              strokeWidth={Math.max(0.5, 0.8 / transform.scale)}
                            />
                            <text
                              x={tx + 3 / transform.scale}
                              y={ty}
                              fill={pathColor}
                              fontSize={fs}
                              fontFamily="Cinzel"
                            >
                              {label}
                            </text>
                          </g>
                        );
                      })()}
                    </g>
                  ))}
                </g>
              )}

              {/* Ruler */}
              {rulerPoints.length === 2 && (
                <g>
                  <line
                    x1={rulerPoints[0].x * imgSize.w}
                    y1={rulerPoints[0].y * imgSize.h}
                    x2={rulerPoints[1].x * imgSize.w}
                    y2={rulerPoints[1].y * imgSize.h}
                    stroke="#f59e0b"
                    strokeWidth={Math.max(2, 3 / transform.scale)}
                    strokeDasharray="6 4"
                  />
                  {rulerPoints.map((p, i) => (
                    <circle key={i} cx={p.x * imgSize.w} cy={p.y * imgSize.h} r={Math.max(3, 5 / transform.scale)} fill="#f59e0b" />
                  ))}
                  {(() => {
                    const label = distanceLabel(rulerPoints[0].x, rulerPoints[0].y, rulerPoints[1].x, rulerPoints[1].y);
                    const fs = Math.max(12, 18 / transform.scale);
                    const w = label.length * fs * 0.6 + 12 / transform.scale;
                    const h = fs + 8 / transform.scale;
                    const cx = ((rulerPoints[0].x + rulerPoints[1].x) / 2) * imgSize.w;
                    const cy = ((rulerPoints[0].y + rulerPoints[1].y) / 2) * imgSize.h - 10 / transform.scale;
                    return (
                      <g>
                        <rect
                          x={cx - w / 2}
                          y={cy - fs}
                          width={w}
                          height={h}
                          rx={4 / transform.scale}
                          fill="rgba(15,12,20,0.9)"
                          stroke="#f59e0b"
                          strokeWidth={Math.max(0.5, 1 / transform.scale)}
                        />
                        <text
                          x={cx}
                          y={cy}
                          fill="#f59e0b"
                          fontSize={fs}
                          fontFamily="Cinzel"
                          textAnchor="middle"
                        >
                          {label}
                        </text>
                      </g>
                    );
                  })()}
                </g>
              )}

              {/* FANTASY PIN LAYER */}
              <g style={{ pointerEvents: pinsInteractive ? "auto" : "none" }}>
                {orderedLocations.map((loc) => {
                  const cat = getCategory(loc.category);
                  const cx = loc.x * imgSize.w;
                  const cy = loc.y * imgSize.h;
                  const scaled = pinSize / transform.scale;
                  const isSelected = loc.id === selectedLocationId;
                  const isHovered = loc.id === hoverLocId;
                  return (
                    <g
                      key={loc.id}
                      style={{ cursor: pinsInteractive ? "pointer" : "default" }}
                      onMouseEnter={() => setHoverLocId(loc.id)}
                      onMouseLeave={() => setHoverLocId(null)}
                      onMouseDown={(e) => {
                        if (!pinsInteractive) return;
                        if (tool === "view" || tool === "pin") {
                          e.stopPropagation();
                          // Register a pending drag - will activate only if mouse moves > threshold
                          pendingDragRef.current = { locId: loc.id, startX: e.clientX, startY: e.clientY };
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // If we dragged, don't fire click
                        if (draggingLocId) return;
                        if (pinsInteractive) onLocationClick?.(loc);
                      }}
                      data-testid={`map-pin-${loc.id}`}
                    >
                      <FantasyPin
                        x={cx}
                        y={cy}
                        size={scaled * 2}
                        category={loc.category}
                        selected={isSelected}
                        highlighted={isHovered}
                        style={pinStyle}
                      />
                      {/* Icon overlay via foreignObject */}
                      <foreignObject
                        x={cx - scaled * 0.6}
                        y={cy - scaled * 0.6}
                        width={scaled * 1.2}
                        height={scaled * 1.2}
                        style={{ pointerEvents: "none" }}
                      >
                        <div className="w-full h-full flex items-center justify-center">
                          <CategoryIcon
                            id={loc.category}
                            className=""
                            style={{
                              width: `${scaled * 0.9}px`,
                              height: `${scaled * 0.9}px`,
                              color: "#0b0507",
                              filter: "drop-shadow(0 0 2px rgba(255,255,255,0.5))",
                            }}
                          />
                        </div>
                      </foreignObject>
                      {/* Label on hover / selected */}
                      {(isHovered || isSelected) && (
                        <g>
                          <rect
                            x={cx - 60 / transform.scale}
                            y={cy + scaled * 1.1}
                            width={120 / transform.scale}
                            height={20 / transform.scale}
                            rx={4 / transform.scale}
                            fill="rgba(15, 12, 20, 0.9)"
                            stroke={cat.color}
                            strokeWidth={Math.max(0.5, 1 / transform.scale)}
                          />
                          <text
                            x={cx}
                            y={cy + scaled * 1.1 + 14 / transform.scale}
                            fill="#f5deb3"
                            fontSize={Math.max(9, 11 / transform.scale)}
                            fontFamily="Cinzel"
                            textAnchor="middle"
                          >
                            {loc.name}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}
              </g>
            </svg>
          )}
        </div>
      )}

      {/* HUD - zoom controls */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-2" data-testid="canvas-hud">
        <button
          className="w-9 h-9 rounded-md bg-card/80 border border-gold/40 hover:border-gold text-gold font-heading text-lg backdrop-blur"
          onClick={() => setTransform((t) => ({ ...t, scale: Math.min(6, t.scale * 1.1) }))}
          data-testid="zoom-in-btn"
        >
          +
        </button>
        <button
          className="w-9 h-9 rounded-md bg-card/80 border border-gold/40 hover:border-gold text-gold font-heading text-lg backdrop-blur"
          onClick={() => setTransform((t) => ({ ...t, scale: Math.max(0.1, t.scale / 1.1) }))}
          data-testid="zoom-out-btn"
        >
          −
        </button>
        <button
          className="w-9 h-9 rounded-md bg-card/80 border border-gold/40 hover:border-gold text-gold font-heading text-xs backdrop-blur"
          onClick={fitToScreen}
          data-testid="zoom-fit-btn"
        >
          Fit
        </button>
      </div>

      {/* Tool hint */}
      {tool !== "view" && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-card/85 backdrop-blur border border-gold/40 font-heading text-xs tracking-widest uppercase text-gold">
          {tool === "pin" && "Modalità Pin: clicca per aggiungere un luogo"}
          {tool === "path" && "Modalità Percorso: clicca per aggiungere punti · trascina per spostarli"}
          {tool === "ruler" && "Righello: clicca due punti per misurare"}
        </div>
      )}

      {/* Night indicator */}
      {isNight && (
        <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-arcane/20 border border-arcane/50 backdrop-blur">
          <span className="font-heading text-[10px] tracking-widest uppercase text-arcane">◐ Ore Notturne</span>
        </div>
      )}
    </div>
  );
}
